import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Activity,
  Download,
  Gauge,
  Map as MapIcon,
  Navigation,
  TimerReset,
} from "lucide-react";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { resolveTrackingState } from "../../utils/trackingStateEngine";
import { resolveVehicleIconAsset } from "../../utils/vehicleIconResolver";
import HistoryFiltersSidebar from "../../components/history/HistoryFiltersSidebar";
import HistoryPlaybackMap from "../../components/history/HistoryPlaybackMap";
import PlaybackController from "../../components/history/PlaybackController";
import HistoryTimelinePanel from "../../components/history/HistoryTimelinePanel";
import "../../components/admin/TripPlayback.css";
import "./history-playback.css";

const MIN_POINT_DELAY_MS = 260;
const MAX_POINT_DELAY_MS = 9000;
const DEFAULT_POINT_DELAY_MS = 900;
const PLAYBACK_SPEEDS = [0.5, 1, 2, 4, 6, 8, 10];
const PLAYBACK_UI_FRAME_MS = 80;
const MAX_MAP_ROUTE_POINTS = 1200;
const HISTORY_IDLE_MAX_SPEED_KMH = 6;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const toRadians = (deg) => (deg * Math.PI) / 180;
const toDegrees = (rad) => (rad * 180) / Math.PI;
const normalizeAngle = (deg) => ((Number(deg) % 360) + 360) % 360;

const haversineMeters = (a, b) => {
  const lat1 = toFiniteNumber(a?.lat);
  const lng1 = toFiniteNumber(a?.lng);
  const lat2 = toFiniteNumber(b?.lat);
  const lng2 = toFiniteNumber(b?.lng);
  if ([lat1, lng1, lat2, lng2].some((value) => value === null)) return 0;

  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
};

const bearingBetween = (a, b) => {
  const lat1 = toFiniteNumber(a?.lat);
  const lng1 = toFiniteNumber(a?.lng);
  const lat2 = toFiniteNumber(b?.lat);
  const lng2 = toFiniteNumber(b?.lng);
  if ([lat1, lng1, lat2, lng2].some((value) => value === null)) return null;
  if (lat1 === lat2 && lng1 === lng2) return null;

  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const lambda1 = toRadians(lng1);
  const lambda2 = toRadians(lng2);
  const y = Math.sin(lambda2 - lambda1) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(lambda2 - lambda1);
  return normalizeAngle(toDegrees(Math.atan2(y, x)));
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDateTime = (value) => {
  const ts = toTimestampMs(value);
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatTimeOnly = (value) => {
  const ts = toTimestampMs(value);
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatDurationLabel = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatStatusLabel = (point) => {
  const explicit = String(point?.vehicleStatus || "").trim();
  if (explicit) return explicit;
  const speed = Number(point?.speed || 0);
  if (speed > 2) return "Running";
  if (point?.accStatus === true) return "Idle";
  return "Stopped";
};

const formatAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();
  if (address.full) return String(address.full).trim();
  return [address.street, address.city, address.state, address.country].filter(Boolean).join(", ");
};

const normalizeRoute = (points = []) => {
  let prevTracking = null;
  return points
    .map((point, index) => {
      const lat = toFiniteNumber(point?.lat ?? point?.latitude);
      const lng = toFiniteNumber(point?.lng ?? point?.longitude);
      if (lat === null || lng === null) return null;

      const tracking = resolveTrackingState({
        prev: prevTracking,
        speed: Number(point?.speed || 0),
        accStatus: point?.accStatus ?? point?.acc ?? point?.ignition ?? null,
        timestampMs: toTimestampMs(point?.timestamp ?? point?.gpsTime ?? point?.packetTime),
        latitude: lat,
        longitude: lng,
        heading: Number(point?.heading ?? point?.course ?? point?.bearing ?? 0),
        vehicleStatus: point?.vehicleStatus ?? point?.status ?? null,
        movementState: point?.movementState ?? point?.movement_state ?? null,
        direction: point?.direction ?? null,
        turnType: point?.turnType ?? point?.turn_type ?? null,
      });
      prevTracking = tracking;

      // Playback gets a definitive engine state from ACC/ignition alarms.
      // At under 6 km/h with the engine on, the vehicle is idling in traffic;
      // with the engine off it is parked/stopped.
      const ignitionOn = tracking.accStatus === true;
      const ignitionOff = tracking.accStatus === false;
      const slowOrStationary = Number(point?.speed || 0) < HISTORY_IDLE_MAX_SPEED_KMH;
      const playbackVehicleStatus = ignitionOn
        ? (slowOrStationary ? "idle" : "running")
        : ignitionOff && slowOrStationary
          ? "stopped"
          : tracking.vehicleStatus;
      const playbackMovementState = ignitionOn
        ? (slowOrStationary ? "idle" : "moving")
        : ignitionOff && slowOrStationary
          ? "stopped"
          : tracking.movementState;

      return {
        ...point,
        sourceIndex: index,
        lat,
        lng,
        speed: Number(point?.speed || 0),
        heading: tracking.heading,
        vehicleStatus: playbackVehicleStatus,
        movementState: playbackMovementState,
        direction: tracking.direction,
        turnType: tracking.turnType,
        accStatus: tracking.accStatus,
        timestampMs: toTimestampMs(point?.timestamp ?? point?.gpsTime ?? point?.packetTime),
        address:
          formatAddress(point?.resolvedAddress) ||
          formatAddress(point?.geocodedAddress) ||
          formatAddress(point?.address) ||
          formatAddress(point?.locationAddress) ||
          formatAddress(point?.currentAddress),
      };
    })
    .filter(Boolean);
};

const sampleRouteForMap = (route = []) => {
  if (route.length <= MAX_MAP_ROUTE_POINTS) return route;
  const step = Math.ceil((route.length - 1) / (MAX_MAP_ROUTE_POINTS - 1));
  const sampled = [];
  for (let index = 0; index < route.length; index += step) sampled.push(route[index]);
  if (sampled[sampled.length - 1] !== route[route.length - 1]) sampled.push(route[route.length - 1]);
  return sampled;
};

const classifyStatusTone = (point, index, routeLength) => {
  if (index === 0) return "start";
  if (index === routeLength - 1) return "end";
  const speed = Number(point?.speed || 0);
  const status = String(formatStatusLabel(point)).toLowerCase();
  if (speed >= 80) return "speeding";
  if (point?.accStatus === true && speed < HISTORY_IDLE_MAX_SPEED_KMH) return "idle";
  if (point?.accStatus === false && speed < HISTORY_IDLE_MAX_SPEED_KMH) return "parking";
  if (status.includes("park")) return "parking";
  if (status.includes("idle")) return "idle";
  if (status.includes("stop")) return "stops";
  if (status.includes("run") || speed > 2) return "running";
  return "default";
};

export default function HistoryPlaybackPage({
  devices = [],
  getVehicleIcon,
  initialTab = "playback",
  initialDeviceId = "",
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: fetchedDevices = [] } = useGetAllLiteDevicesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const requestedDeviceId = useMemo(
    () => String(initialDeviceId || searchParams.get("deviceId") || "").trim(),
    [initialDeviceId, searchParams]
  );
  const resolvedDevices = useMemo(() => {
    const propDevices = Array.isArray(devices) ? devices : [];
    const apiDevices = Array.isArray(fetchedDevices) ? fetchedDevices : [];
    return propDevices.length ? propDevices : apiDevices;
  }, [devices, fetchedDevices]);
  const baseUrl = useMemo(
    () => process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "https://app.krush.in/api",
    []
  );
  const [selectedDeviceId, setSelectedDeviceId] = useState(() => requestedDeviceId);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [stopsFilter, setStopsFilter] = useState("all");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingRouteId, setLoadingRouteId] = useState("");
  const [error, setError] = useState("");
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [route, setRoute] = useState([]);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [segmentProgress, setSegmentProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [mapType, setMapType] = useState("roadmap");
  const [mapInstance, setMapInstance] = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [followVehicle, setFollowVehicle] = useState(true);

  const animationRef = useRef(null);
  const playbackStateRef = useRef({ currentIndex: 0, segmentProgress: 0, playbackSpeed: 1, pointDelays: [] });
  const lastUpdateRef = useRef(Date.now());
  const lastUiUpdateRef = useRef(0);
  const lastMapPanRef = useRef(0);
  const mapShellRef = useRef(null);
  const filterPanelRef = useRef(null);
  const lastAppliedRequestedDeviceIdRef = useRef("");

  useEffect(() => {
    setActiveTab(initialTab === "logs" ? "playback" : initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!requestedDeviceId || lastAppliedRequestedDeviceIdRef.current === requestedDeviceId) return;

    const matchedDevice = resolvedDevices.find((device) => {
      const deviceId = String(device?._id || device?.id || "").trim();
      const deviceImei = String(device?.imei || "").trim();
      return deviceId === requestedDeviceId || deviceImei === requestedDeviceId;
    });
    if (!matchedDevice) return;

    lastAppliedRequestedDeviceIdRef.current = requestedDeviceId;
    setSelectedDeviceId(String(matchedDevice._id || matchedDevice.id || matchedDevice.imei || ""));
  }, [requestedDeviceId, resolvedDevices]);

  useEffect(() => {
    if (selectedDeviceId || !resolvedDevices[0]?._id) return;
    setSelectedDeviceId(String(resolvedDevices[0]._id));
  }, [resolvedDevices, selectedDeviceId]);

  const handleSelectDevice = useCallback((nextDeviceId) => {
    const normalizedDeviceId = String(nextDeviceId || "").trim();
    if (!normalizedDeviceId) return;

    setSelectedDeviceId(normalizedDeviceId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("deviceId", normalizedDeviceId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchTrips = useCallback(async (signal) => {
    if (!selectedDeviceId) return;
    setLoadingTrips(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/history/${selectedDeviceId}/report?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "x-auth-token": token || "",
            "Content-Type": "application/json",
          },
          signal,
        }
      );
      if (!response.ok) throw new Error(`History API failed (${response.status})`);
      const data = await response.json();
      const rides = Array.isArray(data?.rides) ? data.rides : [];
      const dailyData = Array.isArray(data?.dailyData) ? data.dailyData : Array.isArray(data?.trips) ? data.trips : [];
      const rows = (rides.length ? rides : dailyData).map((item, index) => {
        const date = String(item?.date || "");
        const movingTime = Number(item?.movingTime || item?.duration || 0);
        return {
          id: String(item?.id || item?._id || `${date}-${index}`),
          date,
          startTime: item?.startTime || `${date}T00:00:00Z`,
          endTime: item?.endTime || `${date}T23:59:59Z`,
          distance: Number(item?.distanceRawKm ?? item?.distance ?? 0),
          maxSpeed: Number(item?.maxSpeedRawKmh ?? item?.maxSpeed ?? 0),
          avgSpeed: Number(item?.avgSpeedRawKmh ?? item?.avgSpeed ?? 0),
          stoppages: Number(item?.stoppages ?? 0),
          idleTime: Number(item?.idleTime ?? 0),
          durationSeconds: movingTime,
          durationLabel: formatDurationLabel(movingTime),
        };
      });
      setTrips(rows);
      setSelectedTripId(rows[0]?.id || "");
    } catch (fetchError) {
      if (fetchError?.name === "AbortError") return;
      setTrips([]);
      setSelectedTripId("");
      setError(fetchError?.message || "Failed to load history");
    } finally {
      setLoadingTrips(false);
    }
  }, [baseUrl, endDate, selectedDeviceId, startDate]);

  const selectedTrip = useMemo(
    () => trips.find((trip) => String(trip.id) === String(selectedTripId)) || null,
    [selectedTripId, trips]
  );

  const filteredTrips = useMemo(() => {
    const [fromHour, fromMinute] = fromTime.split(":").map((value) => Number(value || 0));
    const [toHour, toMinute] = toTime.split(":").map((value) => Number(value || 0));
    const fromMinutes = fromHour * 60 + fromMinute;
    const toMinutes = toHour * 60 + toMinute;

    return trips.filter((trip) => {
      const start = new Date(trip.startTime);
      const minutes = start.getHours() * 60 + start.getMinutes();
      const matchesStops =
        stopsFilter === "all" ||
        (stopsFilter === "with_stops" && Number(trip.stoppages || 0) > 0) ||
        (stopsFilter === "without_stops" && Number(trip.stoppages || 0) === 0);
      return minutes >= fromMinutes && minutes <= toMinutes && matchesStops;
    });
  }, [fromTime, stopsFilter, toTime, trips]);

  useEffect(() => {
    if (!filteredTrips.length) {
      setSelectedTripId("");
      return;
    }
    const exists = filteredTrips.some((trip) => String(trip.id) === String(selectedTripId));
    if (!exists) {
      setSelectedTripId(filteredTrips[0].id);
    }
  }, [filteredTrips, selectedTripId]);

  const fetchRoute = useCallback(async (trip, signal) => {
    if (!trip || !selectedDeviceId) return;
    setLoadingRouteId(String(trip.id));
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        date: trip.date,
        startTs: new Date(trip.startTime).toISOString(),
        endTs: new Date(trip.endTime).toISOString(),
      });
      const response = await fetch(`${baseUrl}/history/${selectedDeviceId}/playback?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
        signal,
      });
      if (!response.ok) throw new Error(`Playback API failed (${response.status})`);
      const data = await response.json();
      const normalized = normalizeRoute(Array.isArray(data?.points) ? data.points : []);
      // Do not send every GPS point through the Roads API. A long trip can have
      // thousands of samples, which previously created many sequential browser
      // requests and made this page appear frozen. Raw GPS points are sufficient
      // for playback and preserve the complete trip without changing the backend.
      setRoute(normalized);
      setHistoryEvents(Array.isArray(data?.events) ? data.events : []);
      setCurrentIndex(0);
      setSegmentProgress(0);
      setIsPlaying(false);
      setFollowVehicle(true);
    } catch (fetchError) {
      if (fetchError?.name === "AbortError") return;
      setRoute([]);
      setHistoryEvents([]);
      setError(fetchError?.message || "Failed to load playback route");
    } finally {
      setLoadingRouteId("");
    }
  }, [baseUrl, selectedDeviceId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchTrips(controller.signal);
    return () => controller.abort();
  }, [fetchTrips]);

  useEffect(() => {
    if (selectedTrip) {
      const controller = new AbortController();
      fetchRoute(selectedTrip, controller.signal);
      return () => controller.abort();
    }
    setRoute([]);
    return undefined;
  }, [fetchRoute, selectedTrip]);

  const pointDelays = useMemo(() => {
    if (route.length < 2) return [];
    return route.slice(0, -1).map((point, index) => {
      const next = route[index + 1];
      const delta = (toTimestampMs(next?.timestampMs || next?.timestamp) || 0) - (toTimestampMs(point?.timestampMs || point?.timestamp) || 0);
      if (delta > 0) return clamp(delta, MIN_POINT_DELAY_MS, MAX_POINT_DELAY_MS);
      return DEFAULT_POINT_DELAY_MS;
    });
  }, [route]);

  useEffect(() => {
    playbackStateRef.current = {
      currentIndex,
      segmentProgress,
      playbackSpeed,
      pointDelays,
    };
  }, [currentIndex, playbackSpeed, pointDelays, segmentProgress]);

  useEffect(() => {
    if (!isPlaying || route.length < 2) return undefined;

    const animate = () => {
      const now = Date.now();
      let remainingMs = Math.max(0, now - lastUpdateRef.current);
      let idx = playbackStateRef.current.currentIndex;
      let seg = playbackStateRef.current.segmentProgress;

      while (remainingMs > 0 && idx < route.length - 1) {
        const baseDelay = playbackStateRef.current.pointDelays[idx] || DEFAULT_POINT_DELAY_MS;
        const segmentMs = clamp(baseDelay / Math.max(0.5, playbackStateRef.current.playbackSpeed), MIN_POINT_DELAY_MS, MAX_POINT_DELAY_MS);
        const leftMs = (1 - seg) * segmentMs;

        if (remainingMs < leftMs) {
          seg += remainingMs / segmentMs;
          remainingMs = 0;
        } else {
          remainingMs -= leftMs;
          idx += 1;
          seg = 0;
        }
      }

      if (idx >= route.length - 1) {
        setIsPlaying(false);
        idx = route.length - 1;
        seg = 0;
      }

      playbackStateRef.current = {
        ...playbackStateRef.current,
        currentIndex: idx,
        segmentProgress: seg,
      };
      const shouldPaint = now - lastUiUpdateRef.current >= PLAYBACK_UI_FRAME_MS || idx >= route.length - 1;
      if (shouldPaint) {
        setCurrentIndex(idx);
        setSegmentProgress(seg);
        lastUiUpdateRef.current = now;
      }
      lastUpdateRef.current = now;

      if (idx < route.length - 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    lastUpdateRef.current = Date.now();
    lastUiUpdateRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);
    return () => animationRef.current && cancelAnimationFrame(animationRef.current);
  }, [isPlaying, route.length]);

  const currentPosition = useMemo(() => {
    const current = route[currentIndex] || route[0];
    const next = route[Math.min(currentIndex + 1, route.length - 1)] || current;
    if (!current || !next) return null;
    const t = clamp(segmentProgress, 0, 1);
    return {
      lat: Number(current.lat) + (Number(next.lat) - Number(current.lat)) * t,
      lng: Number(current.lng) + (Number(next.lng) - Number(current.lng)) * t,
      speed: Number(current.speed || 0) + (Number(next.speed || 0) - Number(current.speed || 0)) * t,
      heading: bearingBetween(current, next) ?? Number(current.heading || 0),
      vehicleStatus: current.vehicleStatus || "running",
      direction: current.direction || "NE",
      timestamp: current.timestampMs || current.timestamp || null,
      accStatus: current.accStatus,
      voltage: Number(current.voltage || current.power || 12.4),
    };
  }, [currentIndex, route, segmentProgress]);

  useEffect(() => {
    if (!followVehicle || !mapInstance || !currentPosition) return;
    const now = Date.now();
    if (now - lastMapPanRef.current < PLAYBACK_UI_FRAME_MS) return;
    lastMapPanRef.current = now;
    mapInstance.panTo({ lat: currentPosition.lat, lng: currentPosition.lng });
  }, [currentPosition, followVehicle, mapInstance]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const syncFullscreenState = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        null;
      setIsMapFullscreen(fullscreenElement === mapShellRef.current);

      if (fullscreenElement === mapShellRef.current && mapInstance && window.google?.maps?.event) {
        window.google.maps.event.trigger(mapInstance, "resize");
      }
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);
    document.addEventListener("MSFullscreenChange", syncFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener("webkitfullscreenchange", syncFullscreenState);
      document.removeEventListener("MSFullscreenChange", syncFullscreenState);
    };
  }, [mapInstance]);

  useEffect(() => {
    const target = mapShellRef.current;

    return () => {
      if (typeof document === "undefined") return;

      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        null;
      if (target && fullscreenElement === target) {
        const exitFullscreen =
          document.exitFullscreen?.bind(document) ||
          document.webkitExitFullscreen?.bind(document) ||
          document.msExitFullscreen?.bind(document);
        exitFullscreen?.().catch?.(() => {});
      }
    };
  }, []);

  const progress = useMemo(() => {
    if (route.length <= 1) return 0;
    return ((currentIndex + segmentProgress) / (route.length - 1)) * 100;
  }, [currentIndex, route.length, segmentProgress]);

  const routeDistanceKm = useMemo(() => {
    if (selectedTrip?.distance) return Number(selectedTrip.distance);
    let meters = 0;
    for (let index = 1; index < route.length; index += 1) {
      meters += haversineMeters(route[index - 1], route[index]);
    }
    return meters / 1000;
  }, [route, selectedTrip?.distance]);

  const totalDurationSeconds = useMemo(() => {
    if (selectedTrip?.durationSeconds) return Number(selectedTrip.durationSeconds);
    const start = toTimestampMs(route[0]?.timestampMs || route[0]?.timestamp);
    const end = toTimestampMs(route[route.length - 1]?.timestampMs || route[route.length - 1]?.timestamp);
    if (!start || !end || end <= start) return 0;
    return Math.round((end - start) / 1000);
  }, [route, selectedTrip?.durationSeconds]);

  const tripElapsedSeconds = useMemo(() => {
    const start = toTimestampMs(selectedTrip?.startTime || route[0]?.timestampMs || route[0]?.timestamp);
    const end = toTimestampMs(
      selectedTrip?.endTime || route[route.length - 1]?.timestampMs || route[route.length - 1]?.timestamp
    );
    if (!start || !end || end <= start) return 0;
    return Math.round((end - start) / 1000);
  }, [route, selectedTrip?.endTime, selectedTrip?.startTime]);

  const elapsedSeconds = useMemo(
    () => Math.round((clamp(progress, 0, 100) / 100) * totalDurationSeconds),
    [progress, totalDurationSeconds]
  );

  const stops = useMemo(
    () => route.filter((point) => Number(point?.speed || 0) <= 2 && point?.movementState !== "moving").slice(0, 24),
    [route]
  );

  const selectedDevice = useMemo(
    () => resolvedDevices.find((device) => String(device?._id || "") === String(selectedDeviceId || "")) || null,
    [resolvedDevices, selectedDeviceId]
  );

  const playbackMarkerIcon = useMemo(
    () =>
      resolveVehicleIconAsset(selectedDevice, {
        status: currentPosition?.vehicleStatus || selectedTrip?.status || "running",
      }),
    [currentPosition?.vehicleStatus, selectedDevice, selectedTrip?.status]
  );

  const averageSpeedValue = useMemo(() => {
    if (selectedTrip?.avgSpeed) return Math.round(Number(selectedTrip.avgSpeed || 0));
    const movingPoints = route.filter((point) => Number(point?.speed || 0) > 2);
    if (!movingPoints.length) return 0;
    return Math.round(movingPoints.reduce((sum, point) => sum + Number(point.speed || 0), 0) / movingPoints.length);
  }, [route, selectedTrip?.avgSpeed]);

  const summaryItems = useMemo(
    () => [
      ["Start Time", formatDateTime(selectedTrip?.startTime || route[0]?.timestampMs || route[0]?.timestamp)],
      ["End Time", formatDateTime(selectedTrip?.endTime || route[route.length - 1]?.timestampMs || route[route.length - 1]?.timestamp)],
      ["Total Distance", `${routeDistanceKm.toFixed(2)} km`],
      ["Trip Duration", formatDurationLabel(tripElapsedSeconds)],
      ["Driving Time", formatDurationLabel(totalDurationSeconds)],
      ["Max Speed", `${Math.round(Number(selectedTrip?.maxSpeed || 0))} km/h`],
      ["Avg Speed", `${averageSpeedValue} km/h`],
      ["Total Stops", `${stops.length}`],
    ],
    [averageSpeedValue, route, routeDistanceKm, selectedTrip?.endTime, selectedTrip?.maxSpeed, selectedTrip?.startTime, stops.length, totalDurationSeconds, tripElapsedSeconds]
  );

  const topMetricCards = useMemo(
    () => [
      { label: "Total Distance", value: `${routeDistanceKm.toFixed(2)} km`, icon: MapIcon },
      { label: "Driving Time", value: formatDurationLabel(totalDurationSeconds), icon: TimerReset },
      { label: "Max Speed", value: `${Math.round(Number(selectedTrip?.maxSpeed || 0))} km/h`, icon: Gauge },
      { label: "Avg Speed", value: `${averageSpeedValue} km/h`, icon: Navigation },
      { label: "Total Stops", value: `${stops.length}`, icon: Activity },
    ],
    [averageSpeedValue, routeDistanceKm, selectedTrip?.maxSpeed, stops.length, totalDurationSeconds]
  );

  const playbackRows = useMemo(() => {
    const eventByPointIndex = new Map();
    for (const event of historyEvents) {
      const eventTime = toTimestampMs(event?.timestamp);
      if (!eventTime || !route.length) continue;

      let nearestIndex = 0;
      let nearestDelta = Infinity;
      for (let routeIndex = 0; routeIndex < route.length; routeIndex += 1) {
        const delta = Math.abs((route[routeIndex]?.timestampMs || 0) - eventTime);
        if (delta < nearestDelta) {
          nearestIndex = routeIndex;
          nearestDelta = delta;
        }
      }

      const existing = eventByPointIndex.get(nearestIndex) || [];
      existing.push(event);
      eventByPointIndex.set(nearestIndex, existing);
    }

    let cumulativeMeters = 0;
    const points = route.map((point, index) => {
      const next = route[index + 1];
      if (index > 0) cumulativeMeters += haversineMeters(route[index - 1], point);
      const durationSeconds = next?.timestampMs && point?.timestampMs ? Math.max(0, Math.round((next.timestampMs - point.timestampMs) / 1000)) : 0;
      const statusTone = classifyStatusTone(point, index, route.length);
      const statusLabel = index === 0 ? "Start" : index === route.length - 1 ? "End" : formatStatusLabel(point);
      const speedValue = Math.round(Number(point.speed || 0));
      const pointEvents = eventByPointIndex.get(index) || [];
      return {
        id: `${index}-${point.timestampMs || index}`,
        index,
        sequence: index + 1,
        time: formatDateTime(point.timestampMs || point.timestamp),
        timeShort: formatTimeOnly(point.timestampMs || point.timestamp),
        status: statusLabel,
        statusTone,
        filterType: statusTone,
        address: point.address || `${Number(point.lat).toFixed(5)}, ${Number(point.lng).toFixed(5)}`,
        distanceValue: cumulativeMeters / 1000,
        distance: `${(cumulativeMeters / 1000).toFixed(2)} km`,
        speedValue,
        speed: `${speedValue} km/h`,
        durationSeconds,
        duration: durationSeconds > 0 ? formatDurationLabel(durationSeconds) : "--",
        rawPoint: point,
        hasEvent: Boolean(point?.eventType || point?.alarmType || point?.event || pointEvents.length),
        eventTypes: pointEvents.map((event) => event.type),
      };
    });

    if (activeTab === "events") {
      return points.filter((row) => row.hasEvent || row.statusTone === "speeding");
    }
    if (activeTab === "parking") {
      return points.filter((row) => row.statusTone === "parking" || row.statusTone === "stops" || row.statusTone === "idle");
    }
    return points;
  }, [activeTab, historyEvents, route]);

  const mapRoute = useMemo(() => sampleRouteForMap(route), [route]);
  const mapCurrentIndex = useMemo(() => {
    if (mapRoute.length <= 1 || route.length <= 1) return 0;
    return Math.round((clamp(progress, 0, 100) / 100) * (mapRoute.length - 1));
  }, [mapRoute.length, progress, route.length]);

  const progressMarkers = useMemo(() => {
    const markerSource = playbackRows.filter((row, index) => {
      if (index === 0 || index === playbackRows.length - 1) return true;
      return row.statusTone === "stops" || row.statusTone === "parking" || row.statusTone === "speeding" || index % Math.max(1, Math.floor(playbackRows.length / 6)) === 0;
    }).slice(0, 8);

    return markerSource.map((row, index) => ({
      id: row.id,
      index: row.index,
      offset: route.length > 1 ? (row.index / Math.max(1, route.length - 1)) * 100 : 0,
      label: String(index + 1),
    }));
  }, [playbackRows, route.length]);

  const quickRanges = useMemo(
    () => [
      {
        label: "Today",
        onClick: () => {
          setStartDate(today);
          setEndDate(today);
        },
      },
      {
        label: "Yesterday",
        onClick: () => {
          const date = new Date();
          date.setDate(date.getDate() - 1);
          const y = date.toISOString().slice(0, 10);
          setStartDate(y);
          setEndDate(y);
        },
      },
      {
        label: "Last 7 Days",
        onClick: () => {
          const date = new Date();
          const end = new Date();
          date.setDate(date.getDate() - 6);
          setStartDate(date.toISOString().slice(0, 10));
          setEndDate(end.toISOString().slice(0, 10));
        },
      },
      {
        label: "Last 30 Days",
        onClick: () => {
          const date = new Date();
          const end = new Date();
          date.setDate(date.getDate() - 29);
          setStartDate(date.toISOString().slice(0, 10));
          setEndDate(end.toISOString().slice(0, 10));
        },
      },
    ],
    [today]
  );

  const exportRows = useCallback(() => {
    if (!route.length || typeof window === "undefined") return;
    const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = ["Time", "Latitude", "Longitude", "Speed", "Address"];
    const csv = [
      header.map(csvCell).join(","),
      ...route.map((point) =>
        [
          formatDateTime(point.timestampMs || point.timestamp),
          Number(point.lat).toFixed(6),
          Number(point.lng).toFixed(6),
          Math.round(Number(point.speed || 0)),
          point.address || `Coordinates: ${Number(point.lat).toFixed(6)}, ${Number(point.lng).toFixed(6)}`,
        ].map(csvCell).join(",")
      ),
    ].join("\n");
    // BOM lets older Excel versions reliably recognise UTF-8 and quoted cells.
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedDevice?.name || "trip-history"}-${startDate}.csv`;
    link.click();
  }, [route, selectedDevice?.name, startDate]);

  const handleFullscreen = useCallback(() => {
    const target = mapShellRef.current;
    if (!target || typeof document === "undefined") return;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      null;

    if (fullscreenElement === target) {
      const exitFullscreen =
        document.exitFullscreen?.bind(document) ||
        document.webkitExitFullscreen?.bind(document) ||
        document.msExitFullscreen?.bind(document);
      exitFullscreen?.().catch?.(() => {});
      return;
    }

    const requestFullscreen =
      target.requestFullscreen?.bind(target) ||
      target.webkitRequestFullscreen?.bind(target) ||
      target.msRequestFullscreen?.bind(target);

    requestFullscreen?.().catch?.(() => {});
  }, []);

  return (
    <div className="history-page-shell">
      <div className="history-page-grid history-page-grid-redesigned">
        <div ref={filterPanelRef}>
          <HistoryFiltersSidebar
            devices={resolvedDevices}
            selectedDeviceId={selectedDeviceId}
            onSelectDevice={handleSelectDevice}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            fromTime={fromTime}
            toTime={toTime}
            onFromTimeChange={setFromTime}
            onToTimeChange={setToTime}
            stopsFilter={stopsFilter}
            onStopsFilterChange={setStopsFilter}
            onSearch={fetchTrips}
            loading={loadingTrips}
            summaryItems={summaryItems}
            quickRanges={quickRanges}
            trips={filteredTrips}
            selectedTripId={selectedTripId}
            onSelectTrip={(trip) => setSelectedTripId(trip.id)}
            error={error}
          />
        </div>

        <section className="history-workspace">
          <div className="history-topbar-row">
            <div className="history-top-metrics history-top-metrics-expanded">
              {topMetricCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.label} className="history-metric-card">
                    <div className="history-metric-icon">
                      <Icon size={16} />
                    </div>
                    <div>
                      <strong>{card.value}</strong>
                      <span>{card.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="history-action-cluster">
              <button type="button" className="history-primary-button action" onClick={exportRows}>
                <Download size={15} />
                Export Report
              </button>
            </div>
          </div>

          <div className={`history-map-shell ${isMapFullscreen ? "is-map-fullscreen" : ""}`} ref={mapShellRef}>
            <div className="history-map-toggle">
              <button
                type="button"
                className={mapType === "roadmap" ? "active" : ""}
                onClick={() => setMapType("roadmap")}
              >
                Map
              </button>
              <button
                type="button"
                className={mapType === "satellite" ? "active" : ""}
                onClick={() => setMapType("satellite")}
              >
                Satellite
              </button>
            </div>

            <div className="history-map-card history-map-card-redesigned">
              <HistoryPlaybackMap
                route={mapRoute}
                currentIndex={mapCurrentIndex}
                currentPosition={currentPosition}
                stops={stops}
                markerIcon={
                  playbackMarkerIcon ||
                  getVehicleIcon?.(
                    selectedDevice?.iconCategory ??
                      selectedDevice?.vehicleType ??
                      selectedDevice?.deviceIcon ??
                      selectedDevice?.vehicleIcon ??
                      selectedDevice?.icon ??
                      "car"
                  )
                }
                mapType={mapType}
                onMapReady={setMapInstance}
                onManualMapInteraction={() => setFollowVehicle(false)}
              />
              {loadingRouteId ? <div className="history-map-loading">Loading route...</div> : null}
            </div>

            <PlaybackController
              isPlaying={isPlaying}
              onPlayPause={() => {
                if (currentIndex >= route.length - 1) {
                  setCurrentIndex(0);
                  setSegmentProgress(0);
                }
                setIsPlaying((prev) => !prev);
              }}
              onStop={() => {
                setIsPlaying(false);
                setCurrentIndex(0);
                setSegmentProgress(0);
              }}
              onStepBack={() => {
                setIsPlaying(false);
                setSegmentProgress(0);
                setCurrentIndex((value) => Math.max(0, value - 1));
              }}
              onStepForward={() => {
                setIsPlaying(false);
                setSegmentProgress(0);
                setCurrentIndex((value) => Math.min(route.length - 1, value + 1));
              }}
              playbackSpeed={playbackSpeed}
              speedOptions={PLAYBACK_SPEEDS}
              onSpeedChange={setPlaybackSpeed}
              progress={progress}
              onProgressClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                const nextProgress = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
                const scaled = (nextProgress / 100) * Math.max(0, route.length - 1);
                setCurrentIndex(Math.floor(scaled));
                setSegmentProgress(scaled - Math.floor(scaled));
                setIsPlaying(false);
              }}
              elapsedLabel={formatDurationLabel(elapsedSeconds)}
              totalLabel={formatDurationLabel(totalDurationSeconds)}
              onFullscreen={handleFullscreen}
              onCalendar={() => filterPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              isFullscreen={isMapFullscreen}
              followVehicle={followVehicle}
              onFollowToggle={() => {
                setFollowVehicle((current) => {
                  const next = !current;
                  if (next && mapInstance && currentPosition) {
                    mapInstance.panTo({ lat: currentPosition.lat, lng: currentPosition.lng });
                  }
                  return next;
                });
              }}
            />
          </div>

          <HistoryTimelinePanel
            activeTab={activeTab}
            onTabChange={setActiveTab}
            rows={playbackRows}
            currentIndex={currentIndex}
            onJumpToIndex={(index) => {
              setIsPlaying(false);
              setSegmentProgress(0);
              setCurrentIndex(clamp(index, 0, Math.max(0, route.length - 1)));
            }}
            onExport={exportRows}
            markers={progressMarkers}
          />
        </section>
      </div>
    </div>
  );
}
